import { useState } from "react";
import { eur } from "@/lib/api";
import { Card } from "@/components/common";
import { pct, num } from "./helpers";
import { AlertTriangle, Info, ChevronDown } from "lucide-react";

const BUCKETS = [["alta", "Alta"], ["media", "Media"], ["bassa", "Bassa"], ["agosto", "Agosto"]];

export function Warnings({ v }) {
  const w = v?.warnings || [];
  if (!w.length) return null;
  return (
    <Card className="p-4 border-amber-200 bg-amber-50/60" data-testid="pv-warnings">
      <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">Controlli di plausibilità</div>
      <ul className="space-y-1">
        {w.map((x, i) => (
          <li key={i} className={`flex items-start gap-2 text-xs ${x.level === "warn" ? "text-amber-900" : "text-slate-600"}`} data-testid={`pv-warning-${i}`}>
            {x.level === "warn" ? <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" /> : <Info size={13} className="mt-0.5 shrink-0 text-sky-600" />}
            {x.text}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Row({ k, children }) {
  return <div className="flex justify-between gap-3 py-1 border-b border-slate-100"><span className="text-slate-500">{k}</span><span className="text-right font-medium text-slate-800 tabular">{children}</span></div>;
}

export default function Assumptions({ d }) {
  const [open, setOpen] = useState(false);
  const v = d.v2 || {}, tx = v.taxes || {};
  const manual = d.rows.filter((r) => r.manual_est).length;
  return (
    <Card className="p-4" data-testid="pv-assumptions">
      <button data-testid="pv-assumptions-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left">
        <span className="text-sm font-semibold text-slate-700">Ipotesi usate <span className="font-normal text-slate-400">· scenario {v.scenario}</span></span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-3 mt-3 text-xs" data-testid="pv-assumptions-body">
          <div>
            <div className="font-semibold text-slate-600 mb-1">Notti e prenotazioni</div>
            <Row k="Cancellazioni attese">{pct(v.cancel_rate)}<div className="text-[10px] text-slate-400 font-normal">{v.cancel_source}</div></Row>
            <Row k="Tetto pickup senza storico">+{num(((v.pickup_cap || 1.5) - 1) * 100)}% del confermato</Row>
            <Row k="Camere proprie">{d.own_rooms}</Row>
            <Row k="Ospiti medi per camera">{num(v.avg_guests, 2)}</Row>
            <Row k="Mesi su stima manuale">{manual}</Row>
          </div>
          <div>
            <div className="font-semibold text-slate-600 mb-1">Prezzi, commissioni e tasse</div>
            {BUCKETS.map(([b, l]) => <Row key={b} k={`ADR storico ${l}`}>{v.seasonal_adr?.[b] ? eur(v.seasonal_adr[b]) : "nessuno storico"}</Row>)}
            <Row k="Commissioni sul lordo">{pct(v.comm_rate, 2)}</Row>
            <Row k="TGST">{pct(tx.tgst_pct)} sul {pct(tx.tax_base_pct)} del lordo · {tx.taxes_included ? "scorporata" : "aggiunta"}</Row>
            <Row k="Green Tax">{num(tx.green_tax_usd)} $ + {num(tx.green_council_usd)} $ · 1 $ = {num(v.usd_eur, 4)} €</Row>
          </div>
          <div>
            <div className="font-semibold text-slate-600 mb-1">Costi</div>
            {BUCKETS.map(([b, l]) => <Row key={b} k={`Variabili ${l} (a occ. rif.)`}>{v.var_ref?.[b] != null ? eur(v.var_ref[b]) : `voci standard ${eur(v.std_var)}`}</Row>)}
            <Row k="Imprevisti">{pct(d.contingency_pct)} delle spese</Row>
            <Row k="Mesi storici validi">{v.history_months?.length ? v.history_months.join(", ") : "nessuno con notti"}{v.history_empty ? <div className="text-[10px] text-slate-400 font-normal">{v.history_empty} mesi validi ma senza notti</div> : null}</Row>
          </div>
          <p className="lg:col-span-3 text-[11px] text-slate-400">Prudente = confermato meno cancellazioni, senza pickup, variabili +10%. Base = confermato meno cancellazioni + pickup. Ottimistico = pickup ×1,2 entro la capienza, nessuna cancellazione. Mesi anomali e chiusi esclusi da medie, ADR, calibrazione e obiettivo. Fotografia della previsione salvata ogni 1° del mese.</p>
        </div>
      )}
    </Card>
  );
}
